import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";

import { formatDiffSecondToString, remainTime } from "../../../libs/dateFormat";

import { RequestGoodsRentalResponse } from "../../../types/goods";

import {
  RentalStockSummary,
  StoreInfo,
  getStoreList,
} from "../../../services/rentalStockServices";
import {
  ManagerGoodsRentalServiceSummary,
  completeManagerGoodsRental,
  getMangerGoodsRentalList,
  pushManagerGoodsRental,
  rentManagerGoodsRental,
  restoreManagerGoodsRental,
} from "../../../services/managerGoodsRentalServices";

import useFullScreenLoading from "../../../hooks/useFullScreenLoading";
import useInfiniteScroll, {
  INFINITE_CODE,
} from "../../../hooks/useInfiniteScroll";
import useSimplePopup from "../../../hooks/useSimplePopup";

import StickyHeader from "../../../components/Layout/StickyHeader";
import { RentalStockSummaryList } from "../../../components/RentalStock/RentalStockSummaryList";
import CommonButton from "../../../components/Button";
import HorizontalRule from "../../../components/Rule/Horizontal";
import CommonSelect from "../../../components/Select";
import { RentalStockHistorySummaryButton } from "./RentalStock/Control/History";
import ManagerRentalItem from "../../../components/Manager/Rental/Item";
import CommonPopup from "../../../components/Popup";
import BottomGroup from "../../../components/Button/Group";

import { TalkFindBuildingDownArrow } from "../../Talk";
import {
  getLocalValue,
  removeLocalValue,
  setLocalValue,
} from "../../../libs/localStorage";

import { PostListNoPostIcon } from "../../../components/Talk/PostList";
import { RefreshIcon } from "../../../hooks/useStoreHeader";

type Props = {};

export default function ManagerRentalManager(props: Props) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [store, setStore] = useState<StoreInfo>();
  const [storeList, setStoreList] = useState<StoreInfo[]>([]);
  const [rentalStockSummary, setRentalStockSummary] =
    useState<RentalStockSummary>();
  const [rentalServiceSummary, setRentalServiceSummary] =
    useState<ManagerGoodsRentalServiceSummary>();
  const [activeRentalService, setActiveRentalService] =
    useState<string>("totalServiceCount");
  const [rentalList, setRentalList] = useState<RequestGoodsRentalResponse[]>(
    []
  );
  const [selectedItem, setSelectedItem] =
    useState<RequestGoodsRentalResponse>();
  const [popup, setPopup] = React.useState<
    | "checkManualRental"
    | "checkPush"
    | "checkManualReturn"
    | "checkRestore"
    | ""
  >("");
  const [simplePopupComponent, { open }] = useSimplePopup({});

  const loadStoreList = async () => {
    setIsLoading(true);
    try {
      const result = await getStoreList();
      if (result.status === HttpStatusCode.Ok) {
        setStoreList(result.data.items);
      }

      const prevStore = getLocalValue("rentalManagerStore");
      if (prevStore && !store) {
        setStore(result.data.items.find((item) => item.storeId === prevStore));
      }
    } catch (e) {
      console.log("", e);
    } finally {
      setIsLoading(false);
    }
  };

  const [pushMessage, setPushMessage] = useState<string>("");
  const getPushMessage = (item: RequestGoodsRentalResponse) => {
    return `대여하신 ${item.goodsName}이 ${formatDiffSecondToString(
      Math.abs(remainTime(item.returnDueAt ?? "", 0))
    )} 연체 되었어요.\n빌려 가신 ${item.goodsName.trim()}은 다른 분들도 함께 쓰는 소중한 물건이랍니다. :)`;
  };

  const loadRentalStockList = async (params: {
    page?: number;
    pageSize?: number;
  }) => {
    let returnCount = INFINITE_CODE.ERROR;

    setIsLoading(true);
    try {
      const result = await getMangerGoodsRentalList({
        storeId: store?.storeId ?? undefined,
        status: getServiceStatus(activeRentalService),
        ...params,
      });

      if (result.status === HttpStatusCode.Ok) {
        setRentalStockSummary(result.data.stockSummary);
        setRentalServiceSummary(result.data.serviceSummary);

        if (params.page === 1) {
          setRentalList(result.data.goodsRentalServicePageableItems.items);
        } else {
          setRentalList((prev) => [
            ...prev,
            ...result.data.goodsRentalServicePageableItems.items,
          ]);
        }
        returnCount = (result.data.goodsRentalServicePageableItems.items ?? [])
          .length;
        //   setRentalStockList(result.data.items);
        //   const target = result.data.items.find(
        //     (item) => item.stockId === rentalStock?.stockId
        //   );
        //   if (rentalStock === undefined || target === undefined) {
        //     setRentalStock(result.data.items[0]);
        //   } else {
        //     setRentalStock(target);
        //   }
      } else {
        setRentalStockSummary(undefined);
        setRentalServiceSummary(undefined);
        setRentalList([]);
      }
    } catch (e: any) {
      setRentalStockSummary(undefined);
      setRentalServiceSummary(undefined);
      setRentalList([]);
      if (e?.response?.data?.error) {
        open(e.response.data.error);
      } else {
        open("대여 목록 조회에 실패했습니다.");
      }
    } finally {
      setIsLoading(false);
    }
    return returnCount;
  };

  const [, { resetScroll }] = useInfiniteScroll({
    callback: async (args) => {
      return loadRentalStockList(args);
    },
    isLoading: isLoading,
  });

  const getServiceStatus = (key?: string) => {
    switch (key) {
      case "totalServiceCount":
        return undefined;
      case "doneCount":
        return "DONE";
      case "overdueCount":
        return "OVERDUE";
      case "rentedCount":
        return "RENTED";
      case "requestedCount":
        return "REQUESTED";
      default:
        return undefined;
    }
  };

  const getServiceLabel = (key: keyof ManagerGoodsRentalServiceSummary) => {
    switch (key) {
      case "totalServiceCount":
        return "전체";
      case "doneCount":
        return "취소/완료";
      case "overdueCount":
        return "연체";
      case "rentedCount":
        return "대여중";
      case "requestedCount":
        return "신청";
      default:
        return "";
    }
  };

  const handleManualRental = async () => {
    setIsLoading(true);
    try {
      const result = await rentManagerGoodsRental(
        selectedItem?.goodsServiceId ?? ""
      );
      if (result.status === HttpStatusCode.Ok) {
        handleRefresh();
      } else {
        open("수동 대여에 실패했습니다.");
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e.response.data.error);
      } else {
        open("수동 대여에 실패했습니다.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualReturn = async () => {
    setIsLoading(true);
    try {
      const result = await completeManagerGoodsRental(
        selectedItem?.goodsServiceId ?? ""
      );
      if (result.status === HttpStatusCode.Ok) {
        handleRefresh();
      } else {
        open("수동 반납에 실패했습니다.");
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e.response.data.error);
      } else {
        open("수동 반납에 실패했습니다.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestore = async () => {
    setIsLoading(true);
    try {
      const result = await restoreManagerGoodsRental(
        selectedItem?.goodsServiceId ?? ""
      );
      if (result.status === HttpStatusCode.Ok) {
        handleRefresh();
      } else {
        open("정상 반납 처리에 실패했습니다.");
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e.response.data.error);
      } else {
        open("정상 반납 처리에 실패했습니다.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePush = async () => {
    setIsLoading(true);
    try {
      const result = await pushManagerGoodsRental(
        selectedItem?.goodsServiceId ?? "",
        pushMessage
      );
      if (result.status === HttpStatusCode.Ok) {
        handleRefresh();
      } else {
        open("반납 알림 발송에 실패했습니다.");
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e.response.data.error);
      } else {
        open("반납 알림 발송에 실패했습니다.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    resetScroll();
    loadRentalStockList({ page: 1, pageSize: 10 });
  };

  useEffect(() => {
    handleRefresh();
  }, [store, activeRentalService]);

  useEffect(() => {
    loadStoreList();
  }, []);

  return (
    <div>
      {loadingComponent}
      <StickyHeader
        title="대여 관리"
        onBackClick={() => navigate("/view")}
        className="px-5"
        rightContent={
          <div onClick={handleRefresh}>
            <RefreshIcon />
          </div>
        }
      />

      <div className="p-5 flex flex-col gap-2.5">
        <div className="flex gap-2.5">
          <CommonSelect
            defaultValue={"전체 대여 장소"}
            arrowIcon={<TalkFindBuildingDownArrow />}
            value={store?.storeId ?? ""}
            unUsePadding
            useTruncate
            optionList={
              [
                { value: "", label: "전체 대여 장소" },
                ...(storeList?.map((store) => ({
                  value: store.storeId,
                  label: store.storeName,
                })) ?? []),
              ] ?? []
            }
            onChange={(target) => {
              const selectedStore = storeList.find(
                (store) => store.storeId === target.value
              );
              setStore(selectedStore);
              if (selectedStore?.storeId) {
                setLocalValue("rentalManagerStore", selectedStore?.storeId);
              } else {
                removeLocalValue("rentalManagerStore");
              }
            }}
            className="text-3xl font-medium leading-[38px] tracking-[-1.2px]"
            optionOrder="between"
            optionWidth="240px"
            wrapperClassName="w-fit max-w-[calc(100vw-40px)]"
            variant="selectBuilding"
          />
          <span className="text-3xl font-light leading-[38px] tracking-[-1.2px]">
            에
          </span>
        </div>
        <div className=" text-3xl font-light leading-[38px] tracking-[-1.2px]">
          <span className="font-medium">신청</span>이{" "}
          <span className="font-medium">
            {rentalServiceSummary?.requestedCount ?? 0}
          </span>
          건 있어요
        </div>
      </div>

      <div className="p-5 pt-0 flex flex-col gap-2.5">
        <RentalStockSummaryList
          summary={rentalStockSummary}
          wrapperClassName="!p-0"
        />

        <div className="grid grid-cols-3 text-brand-primary-gray-100 gap-2.5">
          <CommonButton
            variant="secondary"
            className="px-4 py-[11px] h-8 text-sm font-medium leading-[17px] whitespace-nowrap"
            onClick={() => {
              navigate("/manager/rental-manager/rental-stock/connect");
            }}
          >
            품목 관리
          </CommonButton>
          <CommonButton
            variant="secondary"
            className="px-4 py-[11px] h-8 text-sm font-medium leading-[17px] whitespace-nowrap"
            onClick={() => {
              navigate("/manager/rental-manager/rental-stock/control");
            }}
          >
            재고 관리
          </CommonButton>
          <CommonButton
            variant="secondary"
            className="px-4 py-[11px] h-8 text-sm font-medium leading-[17px] whitespace-nowrap"
            onClick={() => {
              navigate("/manager/rental-manager/satisfaction");
            }}
          >
            만족도 평가
          </CommonButton>
        </div>
      </div>

      <div className="flex flex-col bg-[#f7f7f7] items-center overscroll-none">
        <div className="px-5 py-2.5 w-full">
          <div className="flex h-[64px] gap-[5px]">
            {rentalServiceSummary &&
              Object.keys(rentalServiceSummary).map((key) => (
                <RentalStockHistorySummaryButton
                  key={key}
                  active={activeRentalService === key}
                  // disabled={rentalStock === undefined}
                  count={
                    rentalServiceSummary[
                      key as keyof ManagerGoodsRentalServiceSummary
                    ] ?? 0
                  }
                  label={getServiceLabel(
                    key as keyof ManagerGoodsRentalServiceSummary
                  )}
                  onClick={() => {
                    setActiveRentalService(key);
                  }}
                />
              ))}
          </div>
        </div>
      </div>

      <div className="px-5 pt-[30px] text-lg font-medium leading-[21px] flex flex-col gap-2.5">
        대여 목록
        <HorizontalRule margin="0" className="bg-black" />
      </div>

      <div className="pt-5 px-5">
        {rentalList && rentalList.length > 0 ? (
          rentalList.map((item, index, items) => (
            <ManagerRentalItem
              key={item.goodsServiceId}
              index={index}
              item={item}
              items={items}
              onClickManualRental={(item) => {
                setSelectedItem(item);
                setPopup("checkManualRental");
              }}
              onClickManualReturn={(item) => {
                setSelectedItem(item);
                setPopup("checkManualReturn");
              }}
              onClickRestore={(item) => {
                setSelectedItem(item);
                setPopup("checkRestore");
              }}
              onClickPush={(item) => {
                setSelectedItem(item);
                setPushMessage(getPushMessage(item));
                setPopup("checkPush");
              }}
            />
          ))
        ) : (
          <div className="flex flex-col  items-center justify-center w-full h-40 gap-3 text-brand-neutral-60">
            <PostListNoPostIcon />
            조회된 내역이 없어요
          </div>
        )}
      </div>

      <CommonPopup isOpen={popup === "checkManualRental"} unUseHorizonRule>
        <div className="pt-5 whitespace-pre-line text-center">{`${selectedItem?.goodsName} (${selectedItem?.createdByName})\n수동 대여 처리 하시겠어요?`}</div>
        <div className="pb-[50px]"></div>
        <BottomGroup zIndex="high">
          <div className="flex flex-row h-bottom-button">
            <CommonButton
              variant="grey"
              className="w-[100px] rounded-none"
              onClick={() => setPopup("")}
            >
              취소
            </CommonButton>
            <CommonButton
              disabled={isLoading}
              className="w-full rounded-none"
              variant="primary"
              onClick={() => {
                handleManualRental();
                setPopup("");
              }}
            >
              확인
            </CommonButton>
          </div>
        </BottomGroup>
      </CommonPopup>

      <CommonPopup
        isOpen={popup === "checkPush"}
        unUseHorizonRule
        title="반납 알림 발송"
      >
        <div className="pb-5 whitespace-pre-line text-center text-xl leading-[24px] font-medium">{`${selectedItem?.goodsName} (${selectedItem?.createdByName})\n반납 알림을 발송하시겠어요?`}</div>
        <div className="flex flex-col gap-2.5">
          <div className="text-sm font-medium">발송 안내 내용</div>
          <div className=" bg-brand-main-page-background p-[15px] rounded-[5px] whitespace-pre-line text-sm leading-[24px] text-brand-neutral-70">
            {pushMessage}
          </div>
        </div>
        <div className="pb-[50px]"></div>
        <BottomGroup zIndex="high">
          <div className="flex flex-row h-bottom-button">
            <CommonButton
              variant="grey"
              className="w-[100px] rounded-none"
              onClick={() => setPopup("")}
            >
              취소
            </CommonButton>
            <CommonButton
              disabled={isLoading}
              className="w-full rounded-none"
              variant="primary"
              onClick={() => {
                handlePush();
                setPopup("");
              }}
            >
              확인
            </CommonButton>
          </div>
        </BottomGroup>
      </CommonPopup>

      <CommonPopup isOpen={popup === "checkManualReturn"} unUseHorizonRule>
        <div className="pt-5 whitespace-pre-line text-center">{`${selectedItem?.goodsName} (${selectedItem?.createdByName})\n수동 반납 처리 하시겠어요?`}</div>
        <div className="pb-[50px]"></div>
        <BottomGroup zIndex="high">
          <div className="flex flex-row h-bottom-button">
            <CommonButton
              variant="grey"
              className="w-[100px] rounded-none"
              onClick={() => setPopup("")}
            >
              취소
            </CommonButton>
            <CommonButton
              disabled={isLoading}
              className="w-full rounded-none"
              variant="primary"
              onClick={() => {
                handleManualReturn();
                setPopup("");
              }}
            >
              확인
            </CommonButton>
          </div>
        </BottomGroup>
      </CommonPopup>

      <CommonPopup isOpen={popup === "checkRestore"} unUseHorizonRule>
        <div className="pt-5 whitespace-pre-line text-center">{`${selectedItem?.goodsName} (${selectedItem?.createdByName})\n정상 반납 처리 하시겠어요?`}</div>
        <div className="pb-[50px]"></div>
        <BottomGroup zIndex="high">
          <div className="flex flex-row h-bottom-button">
            <CommonButton
              variant="grey"
              className="w-[100px] rounded-none"
              onClick={() => setPopup("")}
            >
              취소
            </CommonButton>
            <CommonButton
              disabled={isLoading}
              className="w-full rounded-none"
              variant="primary"
              onClick={() => {
                handleRestore();
                setPopup("");
              }}
            >
              확인
            </CommonButton>
          </div>
        </BottomGroup>
      </CommonPopup>

      {simplePopupComponent}
    </div>
  );
}
